import type { CoverQuizAnswers, CoverRole } from './coverQuizData';
import { L, listJoin } from './quizLang';
import type { QuizLang, Txt } from './quizLang';

/**
 * 匯入客戶資料 — what a finished 保障小測驗 can write into the adviser's
 * factsheet, offered as a list the adviser ticks rather than copied silently.
 *
 * The target is `factsheet.promptInput`, the four fields the A.I. forms
 * (ChatGPTPersonalAnalysis, the ice-breaking drawer) pre-fill from and save
 * back to. The values must be those forms' own vocabulary
 * (ChatGPTPresetDrawer/Inputs/constant.js) — including the misspelt
 * 'Mdeical', which is the stored value there — or the pickers will not show
 * them as ticked.
 *
 * DISC is not here: it has its own 「套用到性格類型」 button on the insights card.
 */

export type PromptInput = {
  lifeStage?: string;
  financialTargets?: string[];
  financialInterests?: string[];
  topics?: string[];
};

export type ImportField = keyof PromptInput;

export type ImportItem = {
  field: ImportField;
  title: string;
  /** What the field holds now, in words; empty when nothing is on file. */
  current: string;
  /** What importing would leave there, in words. */
  next: string;
  /** Replacing an answer the adviser already gave starts unticked. */
  overwrites: boolean;
  value: string | string[];
};

const LIFE_STAGE_LABEL: Record<string, Txt> = {
  'career-start': L('事業起步', '事业起步', 'Career start'),
  'family-growth': L('成家立室', '成家立室', 'Growing family'),
  'pre-retirement': L('準退休族', '准退休族', 'Pre-retirement'),
  retired: L('退休養老', '退休养老', 'Retired')
};
const TARGET_LABEL: Record<string, Txt> = {
  'Entertainment Fund': L('娛樂基金', '娱乐基金', 'Entertainment fund'),
  Marriage: L('結婚', '结婚', 'Marriage'),
  'Property Purchase': L('置業', '置业', 'Buying property'),
  Retired: L('退休', '退休', 'Retirement'),
  'Continuing Education': L('進修', '进修', 'Further studies'),
  Entrepreneurship: L('創業', '创业', 'Starting a business'),
  'Children Education': L('子女教育', '子女教育', "Children's education")
};
const INTEREST_LABEL: Record<string, Txt> = {
  'Life Insurance': L('人壽', '人寿', 'Life'),
  'Critical Illness': L('危疾', '危疾', 'Critical illness'),
  Mdeical: L('醫療', '医疗', 'Medical'),
  Savings: L('儲蓄', '储蓄', 'Savings')
};
const TOPIC_LABEL: Record<string, Txt> = {
  Pets: L('寵物', '宠物', 'Pets'),
  Arts: L('藝術', '艺术', 'Arts'),
  Sports: L('運動', '运动', 'Sports'),
  Media: L('媒體', '媒体', 'Media'),
  Travel: L('旅遊', '旅游', 'Travel')
};
const TITLES = {
  lifeStage: L('人生階段', '人生阶段', 'Life stage'),
  financialTargets: L('財務目標', '财务目标', 'Financial goals'),
  financialInterests: L('關注的保障', '关注的保障', 'Cover of interest'),
  topics: L('興趣話題', '兴趣话题', 'Interests')
};

/** C3 (十年後) and part 2 goals → 財務目標. */
const TARGET_FROM: Record<string, string> = {
  kidsUni: 'Children Education',
  abroad: 'Children Education',
  localUni: 'Children Education',
  edu: 'Children Education',
  biggerHome: 'Property Purchase',
  ownHome: 'Property Purchase',
  home: 'Property Purchase',
  earlyRetire: 'Retired',
  retire: 'Retired',
  familyTravel: 'Entertainment Fund',
  travel: 'Entertainment Fund',
  familyTrip: 'Entertainment Fund',
  business: 'Entrepreneurship',
  study: 'Continuing Education',
  wedding: 'Marriage'
};
/** C6 (最想了解的保障) → 關注的保障. */
const INTEREST_FROM: Record<string, string> = {
  medical: 'Mdeical',
  ci: 'Critical Illness',
  life: 'Life Insurance',
  savings: 'Savings',
  retire: 'Savings',
  edu: 'Savings'
};

const unique = (values: string[]) => values.filter((v, i) => values.indexOf(v) === i);
const words = (values: string[], labels: Record<string, Txt>, lang: QuizLang) =>
  listJoin(
    values.map(v => labels[v]?.[lang] || v),
    lang
  );

/** 身分 → 人生階段. 單身 says nothing about the stage, so it falls through to age. */
const LIFE_STAGE_FROM_ROLE: Partial<Record<CoverRole, string>> = {
  fresh: 'career-start',
  couple: 'family-growth',
  youngKids: 'family-growth',
  schoolKids: 'family-growth',
  preRetire: 'pre-retirement'
};

const lifeStageOf = (answers: CoverQuizAnswers): string | undefined => {
  const fromRole = answers.role && LIFE_STAGE_FROM_ROLE[answers.role];
  if (fromRole) return fromRole;
  if (answers.persona === 'parent') return 'family-growth';
  // An individual's stage is only known when part 2 asked their age.
  const age = answers.goals?.details?.retire?.age;
  if (age === 'a25') return 'career-start';
  if (age === 'a55') return 'pre-retirement';
  return undefined;
};

/** `lang` words the titles and values shown; the stored values are the forms' own. */
export const coverQuizImports = (
  answers: CoverQuizAnswers,
  current: PromptInput = {},
  lang: QuizLang = 'zh-Hant-HK'
): ImportItem[] => {
  const picks = answers.picks || {};
  const multi = answers.multi || {};
  const items: ImportItem[] = [];

  const stage = lifeStageOf(answers);
  if (stage && stage !== current.lifeStage) {
    items.push({
      field: 'lifeStage',
      title: TITLES.lifeStage[lang],
      current: current.lifeStage ? LIFE_STAGE_LABEL[current.lifeStage]?.[lang] || current.lifeStage : '',
      next: LIFE_STAGE_LABEL[stage][lang],
      overwrites: Boolean(current.lifeStage),
      value: stage
    });
  }

  // The lists only ever grow: an import adds what the quiz found and keeps
  // everything the adviser had already picked.
  const addList = (field: Exclude<ImportField, 'lifeStage'>, found: string[], labels: Record<string, Txt>) => {
    const had = (current[field] as string[] | undefined) || [];
    const added = unique(found).filter(v => !had.includes(v));
    if (!added.length) return;
    items.push({
      field,
      title: TITLES[field][lang],
      current: words(had, labels, lang),
      next: `＋ ${words(added, labels, lang)}`,
      overwrites: false,
      value: [...had, ...added]
    });
  };

  const targetKeys = [picks.C3, ...(multi.C6 || []), ...(answers.goals?.picked || [])].filter(Boolean) as string[];
  addList('financialTargets', targetKeys.map(k => TARGET_FROM[k]).filter(Boolean), TARGET_LABEL);
  addList('financialInterests', (multi.C6 || []).map(k => INTEREST_FROM[k]).filter(Boolean), INTEREST_LABEL);

  const topics: string[] = [];
  if (picks.C2 === 'enjoy' || picks.C3 === 'travel' || picks.C3 === 'familyTravel') topics.push('Travel');
  if ((multi.D2 || []).includes('pet')) topics.push('Pets');
  addList('topics', topics, TOPIC_LABEL);

  return items;
};

/** The ticked items, folded onto what is on file — one `promptInput` write. */
export const applyImports = (current: PromptInput = {}, chosen: ImportItem[]): PromptInput =>
  chosen.reduce<PromptInput>((next, item) => ({ ...next, [item.field]: item.value }), { ...current });
